import api from '@/utils/api';
import moment from 'moment';

export const fetchResearches = async (options) => {
  const {
    search, startDate, futureOrWithoutStart, onlyPartnerRequests, page, page_size: pageSize,
  } = options;
  const params = {}
  if (search) params.search = search
  if (startDate) {
    params.start_date_lte = moment(startDate).format('YYYY-MM-DD')
  }
  if (typeof futureOrWithoutStart === 'boolean') {
    params.future_or_without_start = futureOrWithoutStart;
  }
  if (onlyPartnerRequests) params.only_partner_requests = true
  if (page) params.page = page
  if (pageSize) params.page_size = pageSize
  return await api.get('admin/researches/', params);
}

export const fetchResearch = async (researchUUID) => (
  await api.get(`admin/researches/${researchUUID}/`)
)

export const fetchResearchCache = async (researchUUID) => (
  await api.get(`admin/researches/${researchUUID}/cache/`)
)

export const refreshResearchCache = async (researchUUID) => (
  await api.post(`admin/researches/${researchUUID}/cache/refresh/`, {})
)

export const deleteResearchCache = async (researchUUID) => (
  await api.delete(`admin/researches/${researchUUID}/cache/`)
)

export const fetchDashboardReviewsWeekly = async () => (
  await api.get('admin/dashboard/reviews/weekly/')
)

export const fetchDashboardLoginAttemptsWeekly = async () => (
  await api.get('admin/dashboard/login-attempts/weekly/')
)

export const fetchDashboardSummary = async () => (
  await api.get('admin/dashboard/summary/')
)

export const fetchDashboardMailsDaily = async () => (
  await api.get('admin/dashboard/mails/daily/')
)

export const fetchDashboardWeeklyMetrics = async (options = {}) => {
  const { fromDate, toDate } = options;
  return await api.get('admin/dashboard/weekly-metrics/', {
    from_date: fromDate,
    to_date: toDate,
  });
}

export const fetchUpcomingResearches = async () => (
  await api.get('admin/researches/upcoming/')
)

export const unlockResearch = async (researchUUID) => (
  await api.patch(`admin/researches/${researchUUID}/unlock/`)
);

export const exportPlusResearch = async (researchUUID, parts = []) => (
  await api.getFile(
    `/api/admin/researches/${researchUUID}/export-plus/?parts=${encodeURIComponent(parts.join(','))}`,
  )
);

export const createResearchBulkExport = async (researchUUIDs, fileType) => (
  await api.post('admin/research-bulk-exports/', {
    research_uuids: researchUUIDs,
    file_type: fileType,
  })
);

export const fetchResearchBulkExport = async (exportUUID) => (
  await api.get(`admin/research-bulk-exports/${exportUUID}/`)
);

export const downloadResearchBulkExport = async (exportUUID) => (
  await api.getFile(`/api/admin/research-bulk-exports/${exportUUID}/download/`)
);

export const fetchResearchRespondentSummary = async (researchUUID) => (
  await api.get(`admin/researches/${researchUUID}/respondent-summary/`)
);

export const createOrganisationMembership = async (organisationId) => (
  await api.post('admin/organisation-members/', { organisation_id: organisationId })
);

export const fetchOrganisations = async (options) => {
  const { search, onlyAssistants, onlyPartners, page } = options;
  const params = {}
  if (search) params.search = search

  if (onlyAssistants) {
    params.only_assistants = onlyAssistants;
  }
  if (onlyPartners) params.only_partners = true;
  if (page) params.page = page

  return await api.get('admin/organisations/', params);
}

export const updateResearch = async (researchUUID, data) => {
  return await api.patch(`admin/researches/${researchUUID}/`, data);
}

export const chatMessages = {
  getDashboardDaily: async () => {
    return await api.get(
      'admin/dashboard/chat-messages/daily/',
    )
  }
};

export const chatThreads = {
  list: async (options) => {
    const { search, fromDate, toDate, page } = options;
    return await api.get(`admin/chat-threads/`, {
      search,
      from_date: fromDate,
      to_date: toDate,
      page,
    })
  },
  get: async (threadId) => {
    return await api.get(`admin/chat-threads/${threadId}/`);
  },
  listGraphDefinitions: async () => {
    return await api.get('admin/chat-threads/graph-definitions/');
  },
};

export const aiCosts = {
  summary: async (options = {}) => {
    const { fromDate, toDate, groupBy } = options;
    return await api.get('admin/ai-logs/cost-summary/', {
      from_date: fromDate,
      to_date: toDate,
      group_by: groupBy,
    });
  },
};

export const aiLogs = {
  list: async (options) => {
    const { search, page, pageSize } = options;
    return await api.get('admin/ai-logs/', {
      search,
      page,
      page_size: pageSize,
    });
  },
  listRuns: async (options) => {
    const { search, page, pageSize } = options;
    return await api.get('admin/ai-logs/runs/', {
      search,
      page,
      page_size: pageSize,
    });
  },
  getRun: async (graphRunUuid) => {
    return await api.get(`admin/ai-logs/runs/${graphRunUuid}/`);
  },
};

export const aiPrompts = {
  list: async (options = {}) => {
    const { search, type, researchUuid, page } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (type !== undefined && type !== null && type !== '') {
      params.type = type;
    }
    if (researchUuid !== undefined && researchUuid !== null && researchUuid !== '') {
      params.research_uuid = researchUuid;
    }
    if (page !== undefined && page !== null && page !== '') {
      params.page = page;
    }
    return await api.get('admin/ai-prompts/', params);
  },
  get: async (promptId) => {
    return await api.get(`admin/ai-prompts/${promptId}/`);
  },
  create: async (data) => {
    return await api.post('admin/ai-prompts/', data);
  },
  update: async (promptId, data) => {
    return await api.patch(`admin/ai-prompts/${promptId}/`, data);
  },
  listResearches: async (promptId, options = {}) => {
    const { search, page } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (page !== undefined && page !== null && page !== '') {
      params.page = page;
    }
    return await api.get(`admin/ai-prompts/${promptId}/researches/`, params);
  },
  listResearchItems: async (promptId, options = {}) => {
    const { search, page } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (page !== undefined && page !== null && page !== '') {
      params.page = page;
    }
    return await api.get(`admin/ai-prompts/${promptId}/research-items/`, params);
  },
};

export const externalPartySessions = {
  list: async (options) => {
    const { search, startDate, endDate } = options;
    const params = {}
    if (search) params.search = search
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    return await api.get(
      'admin/external-party-sessions/',
      params,
    );
  },
  update: async (options) => {
    const { sessionId, isActive } = options;
    return await api.patch(
      `admin/external-party-sessions/${sessionId}/`,
      { is_active: isActive },
    )
  },
};

export const reviewSessions = {
  list: async (options = {}) => {
    const { search, page, pageSize, dateFrom, dateTo } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (page !== undefined && page !== null && page !== '') {
      params.page = page;
    }
    if (pageSize !== undefined && pageSize !== null && pageSize !== '') {
      params.page_size = pageSize;
    }
    if (dateFrom !== undefined && dateFrom !== null && dateFrom !== '') {
      params.date_from = dateFrom;
    }
    if (dateTo !== undefined && dateTo !== null && dateTo !== '') {
      params.date_to = dateTo;
    }
    return await api.get('admin/research-review-sessions/', params);
  },
  get: async (uuid) => api.get(`admin/research-review-sessions/${uuid}/`),
  recent: async (options = {}) => {
    const { limit } = options;
    return await api.get('admin/research-review-sessions/recent/', { limit: limit || 12 });
  },
  durations: async (researchUuid) => (
    api.get('admin/research-review-sessions/durations/', { research_uuid: researchUuid })
  ),
  dropoff: async (researchUuid) => (
    api.get('admin/research-review-sessions/dropoff/', { research_uuid: researchUuid })
  ),
  externalUids: async (options = {}) => {
    const { search, dateFrom, dateTo } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (dateFrom !== undefined && dateFrom !== null && dateFrom !== '') {
      params.date_from = dateFrom;
    }
    if (dateTo !== undefined && dateTo !== null && dateTo !== '') {
      params.date_to = dateTo;
    }
    return await api.get('admin/research-review-sessions/external-uids/', params);
  },
  normalize: async (uuid) => api.post(`admin/research-review-sessions/${uuid}/normalize/`, {}),
  normalizeAll: async (uuid) => api.post(`admin/research-review-sessions/${uuid}/normalize-all/`, {}),
  normalizeInvalidCount: async (options = {}) => {
    const { search, dateFrom, dateTo } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (dateFrom !== undefined && dateFrom !== null && dateFrom !== '') {
      params.date_from = dateFrom;
    }
    if (dateTo !== undefined && dateTo !== null && dateTo !== '') {
      params.date_to = dateTo;
    }
    return await api.get('admin/research-review-sessions/normalize-invalid/count/', params);
  },
  normalizeInvalid: async (options = {}) => {
    const { search, dateFrom, dateTo } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (dateFrom !== undefined && dateFrom !== null && dateFrom !== '') {
      params.date_from = dateFrom;
    }
    if (dateTo !== undefined && dateTo !== null && dateTo !== '') {
      params.date_to = dateTo;
    }
    return await api.post('admin/research-review-sessions/normalize-invalid/', {}, params);
  },
  backfillInvalidCount: async (options = {}) => {
    const { search, dateFrom, dateTo } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (dateFrom !== undefined && dateFrom !== null && dateFrom !== '') {
      params.date_from = dateFrom;
    }
    if (dateTo !== undefined && dateTo !== null && dateTo !== '') {
      params.date_to = dateTo;
    }
    return await api.get('admin/research-review-sessions/backfill-invalid/count/', params);
  },
  backfillInvalid: async (options = {}) => {
    const { search, dateFrom, dateTo } = options;
    const params = {};
    if (search !== undefined && search !== null && search !== '') {
      params.search = search;
    }
    if (dateFrom !== undefined && dateFrom !== null && dateFrom !== '') {
      params.date_from = dateFrom;
    }
    if (dateTo !== undefined && dateTo !== null && dateTo !== '') {
      params.date_to = dateTo;
    }
    return await api.post('admin/research-review-sessions/backfill-invalid/', {}, params);
  },
};

export const items = {
  stats: async () => {
    return await api.get('admin/items/stats/');
  },
  list: async (options) => {
    const {
      search, is_active: isActive, unused, render_type: renderType, page, page_size: pageSize,
    } = options;
    return await api.get(
      'admin/items/',
      {
        search, is_active: isActive, unused, render_type: renderType, page, page_size: pageSize,
      },
    );
  },
  get: async (itemId) => {
    return await api.get(
      `admin/items/${itemId}/`,
    );
  },
  listAvailableItemSummaryPrompts: async (itemId) => {
    return await api.get(
      `admin/items/${itemId}/available-item-summary-prompts/`,
    );
  },
  review: async (itemId) => {
    return await api.get(
      `admin/items/${itemId}/review/`,
      { limit: 10 },
    );
  },
  mergePreview: async (targetId, sourceIds) => {
    return await api.get(
      `admin/items/${targetId}/merge-preview/`,
      { source_item_ids: sourceIds.join(',') },
    );
  },
  // acknowledgeWarnings: the backend refuses a merge that carries warnings
  // unless the caller confirms it showed them. Only pass true from a flow that
  // rendered the preview warnings to the user.
  merge: async (targetId, sourceIds, acknowledgeWarnings = false) => {
    return await api.post(
      `admin/items/${targetId}/merge/`,
      {
        source_item_ids: sourceIds,
        acknowledge_warnings: acknowledgeWarnings,
      },
    );
  },
  deactivate: async (itemId) => {
    return await api.post(
      `admin/items/${itemId}/deactivate/`,
      {},
    );
  },
  addPrompt: async (itemId, promptId) => {
    return await api.post(
      `admin/items/${itemId}/prompts/`,
      { prompt_id: promptId },
    );
  },
  removePrompt: async (itemId, promptId) => {
    return await api.post(
      `admin/items/${itemId}/prompts/remove/`,
      { prompt_id: promptId },
    );
  },
  update: async (itemId, formData) => {
    return await api.patch(
      `admin/items/${itemId}/`,
      formData,
      { 'Content-Type': 'multipart/form-data' },
    );
  },
}

export const countries = {
  list: async (options) => {
    const { search, page } = options;
    return await api.get(
      'admin/countries/',
      { search, page },
    );
  },
  get: async (countryCode) => {
    return await api.get(
      `admin/countries/${countryCode}/`,
    );
  },
  synchronize: async (countryCode) => {
    return await api.post(
      `admin/countries/${countryCode}/synchronize/`,
      {},
    );
  },
  createAttribute: async (countryCode, localeCode, key) => {
    return await api.post(
      `admin/countries/${countryCode}/attributes/`,
      { locale_code: localeCode, key },
    );
  },
  deleteAttribute: async (countryCode, attributeId) => {
    return await api.delete(
      `admin/countries/${countryCode}/attributes/${attributeId}/`,
    );
  },
  updateAttribute: async (countryCode, attributeId, data) => {
    return await api.patch(
      `admin/countries/${countryCode}/attributes/${attributeId}/`,
      data,
    );
  },
}

export const newsletterRegistrations = {
  list: async (options) => {
    const { search, page } = options;
    return await api.get(
      'admin/newsletter-registrations/',
      { search, page },
    );
  },
}

export const userEvents = {
  list: async (options) => {
    const { search, page } = options;
    return await api.get(
      'admin/user-events/',
      { search, page },
    );
  },
}

export const researchSets = {
  list: async (options) => {
    const { search, page } = options;
    return await api.get(
      'admin/research-sets/',
      { search, page },
    );
  },
  create: async (data) => {
    return await api.post('admin/research-sets/', data);
  },
  get: async (researchSetUUID) => {
    return await api.get(`admin/research-sets/${researchSetUUID}/`);
  },
  update: async (researchSetUUID, data) => {
    return await api.patch(`admin/research-sets/${researchSetUUID}/`, data);
  },
  addResearch: async (researchSetUUID, researchUuid) => {
    return await api.post(
      `admin/research-sets/${researchSetUUID}/researches/`,
      { research_uuid: researchUuid },
    );
  },
}

export const researchSetIdentifiers = {
  create: async (researchSetUUID) => {
    return await api.post('admin/research-set-identifiers/', { research_set: researchSetUUID });
  },
}

export const researchSetResearches = {
  update: async (researchSetResearchId, data) => {
    return await api.patch(`admin/research-set-researches/${researchSetResearchId}/`, data);
  },
}

export const organisationPartnerships = {
  list: async ({ partnerId }) => (
    await api.get('admin/organisation-partnerships/', { partner_id: partnerId })
  ),
  create: async ({ partnerId, brandId }) => (
    await api.post('admin/organisation-partnerships/', {
      partner_id: partnerId,
      brand_id: brandId,
    })
  ),
  delete: async (partnershipId) => (
    await api.delete(`admin/organisation-partnerships/${partnershipId}/`)
  ),
};

export const organisations = {
  get: async (organisationId) => {
    return await api.get(`admin/organisations/${organisationId}/`);
  },
  generateProfile: async (organisationId, attribute) => {
    const data = {}
    if (attribute) data.attribute = attribute
    return await api.post(`admin/organisations/${organisationId}/generate-profile/`, data);
  },
  update: async (organisationId, formData) => {
    return await api.patch(`admin/organisations/${organisationId}/`, formData);
  }
}

export const organisationAssistants = {
  list: async (options) => {
    const { organisationId } = options;
    return await api.get(
      `admin/organisation-assistants/`,
      {
        organisation_id: organisationId || '',
      },
    );
  },
  get: async (assistantId) => {
    return api.get(`admin/organisation-assistants/${assistantId}/`);
  },
  synchronize: async (assistantId) => {
    return await api.patch(
      `admin/organisation-assistants/${assistantId}/synchronize/`,
    )
  },
  update: async (assistantId, formData) => {
    const { instructions, suggestionsEnabled } = formData;
    return await api.patch(
      `admin/organisation-assistants/${assistantId}/`,
      {
        instructions,
        suggestions_enabled: suggestionsEnabled,
      },
    )
  },
  validate: async (assistantId) => {
    return await api.get(
      `admin/organisation-assistants/${assistantId}/validate/`,
    )
  },
}

export const organisationDocuments = {
  download: async (documentId) => {
    return await api.get(
      `admin/organisation-documents/${documentId}/download/`,
    );
  },
  get: async (documentId) => {
    return await api.get(
      `admin/organisation-documents/${documentId}/`,
    );
  },
  // Upload a document for an organisation, linked at once to researches and
  // proposals of it (the documents tab of a research or proposal in the admin);
  create: async ({
    organisationId,
    title,
    file,
    description,
    researchIds = [],
    researchProposalIds = [],
  }) => {
    // One form field per id, as DRF reads a list from a multipart form;
    const formData = new FormData();
    Object.entries({
      organisation_id: organisationId,
      title,
      file,
      description,
    }).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    researchIds.forEach((id) => formData.append('research_ids', id));
    researchProposalIds.forEach((id) => formData.append('research_proposal_ids', id));
    return await api.post(
      `admin/organisation-documents/`,
      formData,
      {},
      {
        'Content-Type': 'multipart/form-data',
      },
    );
  },
  list: async ({
    search,
    organisationId,
    assistantId,
    page,
    pageSize,
    relatedToResearchId,
    relatedToResearchProposalId,
  }) => {
    const params = {};
    if (search) {
      params.search = search;
    }
    if (organisationId) {
      params.organisation_id = organisationId;
    }
    if (assistantId) {
      params.assistant_id = assistantId;
    }
    // What a research's or a proposal's page shows: also the documents of its
    // proposal, resp. of its researches;
    if (relatedToResearchId) {
      params.related_to_research_id = relatedToResearchId;
    }
    if (relatedToResearchProposalId) {
      params.related_to_research_proposal_id = relatedToResearchProposalId;
    }
    if (pageSize) {
      params.page_size = pageSize;
    }
    if (page) {
      params.page = page;
    }
    return await api.get(
      `admin/organisation-documents/`,
      params,
    );
  },
  synchronize: async (documentId) => {
    return await api.patch(
      `admin/organisation-documents/${documentId}/synchronize/`,
    )
  },
  update: async (documentId, formData) => {
    const { title, description, content } = formData;
    return await api.patch(
      `admin/organisation-documents/${documentId}/`,
      {
        title,
        description,
        content,
      },
    );
  },
  // Replaces the links that are given, so also unlinks; leave one out to keep
  // it as it is. Unlinking is only possible here, not on the organisation API;
  updateLinks: async (documentId, { researchIds, researchProposalIds }) => {
    const data = {};
    if (researchIds) {
      data.research_ids = researchIds;
    }
    if (researchProposalIds) {
      data.research_proposal_ids = researchProposalIds;
    }
    return await api.patch(
      `admin/organisation-documents/${documentId}/`,
      data,
    );
  },
};

export const adminOrganisationMembers = {
  list: async (options) => {
    const { organisationId } = options;
    return await api.get(
      `admin/organisation-members/`,
      { organisation_id: organisationId },
    );
  },
}

export const researches = {
  copy: async (researchUUID, formData) => {
    const { organisationId, title, startDate, includeResearchProposal } = formData;
    return await api.post(
      `admin/researches/${researchUUID}/copy/`,
      {
        organisation_id: organisationId,
        title,
        start_date: startDate,
        include_research_proposal: includeResearchProposal,
      },
    );
  },
}

export const researchProposals = {
  list: async (options) => {
    const {
      search,
      source,
      status,
      onlyPartnerRequests,
      page,
    } = options || {};
    const params = {};
    if (search) params.search = search;
    if (source) params.source = source;
    if (status) params.status = status;
    if (onlyPartnerRequests) params.only_partner_requests = true;
    if (page) params.page = page;
    return await api.get(
      'admin/research-proposals/',
      params,
    );
  },
  incoming: async (options = {}) => {
    const { limit } = options;
    return await api.get(
      'admin/research-proposals/incoming/',
      { limit: limit || 8 },
    );
  },
  openCount: async () => {
    return await api.get('admin/research-proposals/open-count/');
  },
  get: async (proposalId) => {
    return await api.get(`admin/research-proposals/${proposalId}/`);
  },
  update: async (proposalId, formData) => {
    return await api.patch(`admin/research-proposals/${proposalId}/`, formData);
  },
  availableResearches: async (proposalId, options = {}) => {
    const { search } = options;
    return await api.get(
      `admin/research-proposals/${proposalId}/available-researches/`,
      { search },
    );
  },
  addResearch: async (proposalId, researchUUID) => {
    return await api.post(
      `admin/research-proposals/${proposalId}/add-research/`,
      { research_uuid: researchUUID },
    );
  },
  removeResearch: async (proposalId, researchUUID) => {
    return await api.post(
      `admin/research-proposals/${proposalId}/remove-research/`,
      { research_uuid: researchUUID },
    );
  },
}

export const researchInviteLinks = {
  list: async ({ researchId, status, page }) => (
    await api.get('admin/research-invite-links/', {
      research_id: researchId,
      status: status || undefined,
      page,
    })
  ),
  create: async ({
    researchId, endDate, title, showOrganisation, maxUses,
  }) => (
    await api.post('admin/research-invite-links/', {
      research_id: researchId,
      end_date: endDate,
      title,
      show_organisation: showOrganisation,
      max_uses: maxUses,
    })
  ),
  update: async (linkId, data) => (
    await api.patch(`admin/research-invite-links/${linkId}/`, data)
  ),
  delete: async (linkId) => (
    await api.delete(`admin/research-invite-links/${linkId}/`)
  ),
};

// The OpenAPI schema of one namespace of the API, for the API documentation;
export const fetchApiSchema = async (namespace) => (
  await api.get('admin/schema/', { namespace, format: 'json' })
);
